import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import BigNumber from 'bignumber.js';
import { EditIcon } from '@/icons';
import { FormValue } from '../type';
import { usePairsByIndex } from '../hooks/usePairsByIndex';
import { usePriceByIndex } from '../hooks/usePriceByIndex';
import { useTPSLV2 } from '../hooks/useTPSLV2';
import Leverage from '../leverage';
import { Amount } from './amount';
import { ButtonSubmit } from './button-submit';
import { Footer } from './footer';
import { PotentialEarning } from './potential-earning';
import { TpSlPanel } from './tp-sl-panel';
import { useSearchParams } from 'react-router';

export function Detail(props: { onClose: () => void }) {
    const { onClose } = props;
    const { t } = useTranslation();
    const { control } = useFormContext<FormValue>();
    const [searchParams] = useSearchParams();

    const {
        errorTP,
        errorSl,
        handleClear,
        stopLossPrice,
        takeProfitPrice,
        handleSetTpPrice,
        handleSetSlPrice,
        setStopLossPercent,
        setTakeProfitPercent,
        stopLossPercent,
        takeProfitPercent,
    } = useTPSLV2();

    const [pairIndex] = useWatch({ control, name: ['pairIndex'] });
    const pair = usePairsByIndex(pairIndex);
    const price = usePriceByIndex(pairIndex);

    const step = useMemo(() => {
        if (!price || price >= 1) return 1;
        return Math.pow(10, -(pair?.decimalsToShow ?? 2));
    }, [price, pair?.decimalsToShow]);

    const adjustPrice = useCallback(
        (current?: number, delta?: number): string => {
            if (!current || !delta) return '0';
            const decimals = pair?.decimalsToShow ?? 2;
            const result = BigNumber.max(
                0,
                new BigNumber(current || 0).plus(delta)
            );
            return result.toFixed(decimals);
        },
        [pair?.decimalsToShow]
    );

    const [isOpenTpSl, setIsOpenTpSl] = useState(false);

    return (
        <div className="pb-10">
            <div className="px-6 pb-2 flex items-center justify-between sticky top-4 bg-white z-10">
                <div className="flex items-center gap-1 group">
                    <h3 className="text-lg font-bold text-slate-900">
                        {t('Market Execution')}
                    </h3>
                </div>
                <button
                    onClick={() => {
                        if (isOpenTpSl) {
                            handleClear('tp');
                            handleClear('sl');
                        }
                        setIsOpenTpSl(!isOpenTpSl);
                    }}
                    className={clsx(
                        'px-3 py-1.5 rounded-full shadow-sm flex items-center justify-center transition-all gap-1 active:scale-95',
                        {
                            'bg-blue-500 text-white': isOpenTpSl,
                            'text-slate-500 hover:text-slate-900 bg-surface-light':
                                !isOpenTpSl,
                        }
                    )}
                >
                    <span className="text-[11px] font-bold">TP / SL</span>
                    <EditIcon className="w-4 h-4" />
                </button>
            </div>
            <div className="px-6 pt-2 pb-4 space-y-4">
                <Amount />
                <Leverage />
                {searchParams.get('suggest') ? <PotentialEarning /> : null}
                <TpSlPanel
                    isOpen={isOpenTpSl}
                    step={step}
                    adjustPrice={adjustPrice}
                    errorTP={errorTP}
                    errorSl={errorSl}
                    handleClear={handleClear}
                    stopLossPrice={stopLossPrice}
                    takeProfitPrice={takeProfitPrice}
                    handleSetTpPrice={handleSetTpPrice}
                    handleSetSlPrice={handleSetSlPrice}
                    setStopLossPercent={setStopLossPercent}
                    setTakeProfitPercent={setTakeProfitPercent}
                    stopLossPercent={stopLossPercent}
                    takeProfitPercent={takeProfitPercent}
                />
                <ButtonSubmit
                    disabled={
                        (Boolean(errorSl) && Boolean(stopLossPrice)) ||
                        (Boolean(errorTP) && Boolean(takeProfitPrice))
                    }
                    onClose={onClose}
                />
                <Footer />
            </div>
        </div>
    );
}
